import NextAuth, {CredentialsSignin} from "next-auth";
import {type DefaultSession} from "next-auth";
import {JWT} from "next-auth/jwt";
import Credentials from "next-auth/providers/credentials";

import {signInSchema} from "@/lib/zod";
import {JwtPayload, jwtDecode} from "jwt-decode";
import {ZodError} from "zod";

import {DataAccess, PermissionProps} from "./types";

import api, {ApiError} from "@/lib/api";

// Custom error class that can carry the actual error message from the backend
class CustomError extends CredentialsSignin {
  code: string;

  constructor(code: string = "An unexpected error has occurred.") {
    super();
    this.code = code;
  }
}

declare module "next-auth" {
  /**
   * Returned by `auth`, `useSession`, `getSession` and received as a prop on the `SessionProvider` React Context
   */
  interface Session {
    error?: "RefreshTokenError";
    user: {
      is_staff: boolean;
      initials: string; // & { length: 2 };
      data_accesses: DataAccess;
      permissions: PermissionProps[];
      access: string;
      refresh: string;
      expires_at: number;
    } & DefaultSession["user"];
  }

  interface User {
    is_staff: boolean;
    initials: string;
    data_accesses: DataAccess;
    permissions: PermissionProps[];
    access: string;
    refresh: string;
    expires_at: number;
  }
}

declare module "next-auth/jwt" {
  /** Returned by the `jwt` callback and `auth`, when using JWT sessions */
  interface JWT {
    user_id: string;
    is_staff: boolean;
    initials: string;
    data_accesses: DataAccess;
    permissions: PermissionProps[];
    access: string;
    expires_at: number;
    refresh: string;
    error?: "RefreshTokenError";
  }
}

interface UserJwtPayload extends JwtPayload {
  user_id: string;
  is_staff: boolean;
  initials: string;
  data_accesses: DataAccess;
  permissions: PermissionProps[];
}

export const {handlers, signIn, signOut, auth} = NextAuth({
  session: {
    strategy: "jwt",
    maxAge: parseInt(process.env.REFRESH_TOKEN_MAX_AGE || "600", 10), // Default to 600 seconds if env variable is undefined
  },
  pages: {
    signIn: "/auth/sign-in",
  },
  providers: [
    Credentials({
      credentials: {
        email: {label: "Email", type: "email", placeholder: "Email"},
        password: {label: "Password", type: "password"},
        recaptcha: {},
      },
      authorize: async (credentials) => {
        try {
          const {email, password, recaptcha} =
            await signInSchema.parseAsync(credentials);

          const data = await api.post("/sign-in/", {
            email,
            password,
            recaptcha,
          });

          // @ts-expect-error TODO: should be fixed
          const decoded = jwtDecode<UserJwtPayload>(data.access);

          // return user object
          return {
            // @ts-expect-error TODO: should be fixed
            ...data,
            id: decoded.user_id,
            is_staff: decoded.is_staff,
            initials: decoded.initials,
            data_accesses: decoded.data_accesses,
            permissions: decoded.permissions,
            expires_at: decoded.exp,
          };
        } catch (error) {
          if (error instanceof ZodError) {
            console.error("Validation failed: ", error);

            const formattedErrors = error.errors
              .map((err) => `${err.path.join(".")}: ${err.message}`)
              .join("; ");
            throw new CustomError(`Validation failed: ${formattedErrors}`);
          } else if (error instanceof ApiError) {
            console.error("Authentication failed: ", error);

            if (
              error.data &&
              typeof error.data === "object" &&
              "detail" in error.data
            ) {
              throw new CustomError(String(error.data.detail));
            }
            // Handle reCAPTCHA validation errors
            if (
              error.data &&
              typeof error.data === "object" &&
              "recaptcha" in error.data
            ) {
              const recaptchaErrors = error.data.recaptcha;
              if (
                Array.isArray(recaptchaErrors) &&
                recaptchaErrors.length > 0
              ) {
                throw new CustomError(recaptchaErrors[0]);
              }
            }
          } else {
            console.error(
              "Authentication failed with unknown error type:",
              error
            );

            throw new CustomError();
          }
          // Return `null` to indicate that the credentials are invalid
          return null;
        }
      },
    }),
  ],
  callbacks: {
    authorized: async ({auth}) => {
      // Logged in users are authenticated, otherwise redirect to login page
      return !!auth;
    },
    async jwt({token, user, account}) {
      // First-time login, save the `access` token, its expiry and the `refresh` token
      if (account) {
        return {
          ...token,
          user_id: user.id,
          is_staff: user.is_staff,
          initials: user.initials,
          data_accesses: user.data_accesses,
          permissions: user.permissions,
          access: user.access,
          expires_at: user.expires_at,
          refresh: user.refresh,
        } as JWT;
      }

      // Subsequent logins, but the `access` token is still valid
      if (Date.now() < token.expires_at * 1000) return token;

      // Subsequent logins, but the `access` has expired, try to refresh it
      if (!token.refresh) throw new TypeError("Missing refresh token");

      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}users/token/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({refresh: token.refresh}),
          }
        );

        if (!res.ok) {
          throw new Error("Refresh failed");
        }

        const data = await res.json();

        // Decode new token to get new expiration
        const decoded = jwtDecode<UserJwtPayload>(data.access);

        return {
          ...token,
          access: data.access,
          expires_at: decoded.exp,
        } as JWT;
      } catch (error) {
        return {
          // If we fail to refresh the token, return an error so we can handle it on the page
          ...token,
          error: "RefreshTokenError",
        };
      }
    },
    async session({session, token}) {
      // If there's a token error, end the session
      // if (token.error) return null;

      session.error = token.error;
      return {
        ...session,
        user: {
          ...session.user,
          id: token.user_id,
          is_staff: token.is_staff,
          initials: token.initials,
          data_accesses: token.data_accesses,
          permissions: token.permissions,
          access: token.access,
        },
      };
    },
  },
});
